'use client'
import React from 'react';
import { motion } from 'motion/react';
import { Brain, MessageSquare, Eye, TrendingUp, Zap, Shield } from 'lucide-react';
import { TCompaniesConfig } from '@/app/config';

const CapabilitiesSection= ({config}: {config: TCompaniesConfig["CapabilitiesSection"]}) => {
  const capabilitiesicon = [
    {
      icon: Brain,
     
    },
    {
      icon: MessageSquare,
      
    },
    {
      icon: Eye,
      
    },
    {
      icon: TrendingUp,
    
    },
    {
      icon: Zap,
      
    },
    {
      icon: Shield,
    
    }
  ];
  const capabilities = config?.capabilities

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };



  const cardHoverVariants = {
    hover: {
      y: -10,
      scale: 1.02,
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

 
  const titleVariants = {
    hover: {
      color: "#16a34a",
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <motion.section 
      className="py-20 bg-gray-50"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
    >
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
           {config?.title}
          </motion.h2>
          <motion.p 
            className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
              {config?.description}
          </motion.p>
        </motion.div>

        {/* Capabilities Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={containerVariants}
        >
          {capabilities.map((capability, index) => {
            const IconComponent = capabilitiesicon[index].icon;
            return (
              <motion.div 
                key={index} 
                className="bg-white rounded-2xl p-8 shadow-sm cursor-pointer"
                variants={{
                  hidden: { 
                    opacity: 0, 
                    y: 50,
                    scale: 0.9
                  },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut"
                    }
                  }
                }}
                whileHover="hover"
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                {...cardHoverVariants}
              >
                <motion.div 
                  className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6"
                  variants={{
                    hidden: { scale: 0, rotate: -180 },
                    visible: {
                      scale: 1,
                      rotate: 0,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut",
                        delay: 0.2
                      }
                    },
                    hover: {
                      scale: 1.1,
                      rotate: [0, -10, 10, -5, 5, 0],
                      transition: {
                        duration: 0.5,
                        ease: "easeInOut"
                      }
                    }
                  }}
                  whileHover="hover"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <IconComponent className="w-6 h-6 text-green-600" />
                </motion.div>
                
                <motion.h3 
                  className="text-xl font-semibold text-gray-900 mb-3"
                  variants={ {
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.4,
                        ease: "easeOut",
                        delay: 0.3
                      }
                    }
                  }}
                  whileHover={titleVariants.hover}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                >
                  {capability.title}
                </motion.h3>
                
                <motion.p 
                  className="text-gray-600 leading-relaxed"
                  variants={ {
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.4,
                        ease: "easeOut",
                        delay: 0.3
                      }
                    }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                >
                  {capability.description}
                </motion.p>

                {/* Subtle background animation on hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-green-50 to-transparent rounded-2xl opacity-0"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ zIndex: -1 }}
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Floating animation elements for visual enhancement */}
        <motion.div 
          className="absolute inset-0 overflow-hidden pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-green-200 rounded-full"
              style={{
                left: `${20 + i * 30}%`,
                top: `${10 + i * 20}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut"
              }}
            />
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default CapabilitiesSection;